import { useState, createElement, useMemo, useEffect } from 'react';
import { supportedProperty, supportedValue } from 'css-vendor';
import { omitProps } from '@team-griffin/rehook';

function _typeof(obj) {
  if (typeof Symbol === "function" && typeof Symbol.iterator === "symbol") {
    _typeof = function (obj) {
      return typeof obj;
    };
  } else {
    _typeof = function (obj) {
      return obj && typeof Symbol === "function" && obj.constructor === Symbol && obj !== Symbol.prototype ? "symbol" : typeof obj;
    };
  }

  return _typeof(obj);
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

function _slicedToArray(arr, i) {
  return _arrayWithHoles(arr) || _iterableToArrayLimit(arr, i) || _nonIterableRest();
}

function _arrayWithHoles(arr) {
  if (Array.isArray(arr)) return arr;
}

function _iterableToArrayLimit(arr, i) {
  var _arr = [];
  var _n = true;
  var _d = false;
  var _e = undefined;

  try {
    for (var _i = arr[Symbol.iterator](), _s; !(_n = (_s = _i.next()).done); _n = true) {
      _arr.push(_s.value);

      if (i && _arr.length === i) break;
    }
  } catch (err) {
    _d = true;
    _e = err;
  } finally {
    try {
      if (!_n && _i["return"] != null) _i["return"]();
    } finally {
      if (_d) throw _e;
    }
  }

  return _arr;
}

function _nonIterableRest() {
  throw new TypeError("Invalid attempt to destructure non-iterable instance");
}

var withInteraction = function withInteraction(Component) {
  return function (ownerProps) {
    var _useState = useState(false),
        _useState2 = _slicedToArray(_useState, 2),
        hover = _useState2[0],
        setHover = _useState2[1];

    var _useState3 = useState(false),
        _useState4 = _slicedToArray(_useState3, 2),
        focus = _useState4[0],
        setFocus = _useState4[1];

    var _useState5 = useState(false),
        _useState6 = _slicedToArray(_useState5, 2),
        active = _useState6[0],
        setActive = _useState6[1];

    var handleMouseOver = function handleMouseOver() {
      return setHover(true);
    };

    var handleMouseLeave = function handleMouseLeave() {
      return setHover(false);
    };

    var handleFocus = function handleFocus() {
      return setFocus(true);
    };

    var handleBlur = function handleBlur() {
      return setFocus(false);
    };

    var handleMouseDown = function handleMouseDown() {
      return setActive(true);
    };

    var handleMouseUp = function handleMouseUp() {
      return setActive(false);
    };

    var hocus = focus || hover;
    var props = Object.assign({}, ownerProps, {
      hover: hover,
      focus: focus,
      active: active,
      hocus: hocus
    });
    return createElement('span', {
      onMouseOver: handleMouseOver,
      onMouseLeave: handleMouseLeave,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp
    }, createElement(Component, props));
  };
};

var Primitives = ['string', 'number', 'boolean', 'undefined', 'bigint'];
var isPrimitive = function isPrimitive(x) {
  var t = _typeof(x);

  return Primitives.indexOf(t) >= 0;
};
var mergeDeep = function mergeDeep(left, right) {
  var result = Object.assign({}, left, right);
  Object.keys(right).forEach(function (key) {
    var l = left[key];
    var r = right[key];

    if (l != null && _typeof(l) === 'object') {
      // @ts-ignore
      result[key] = mergeDeep(l, r);
    }
  });
  return result;
};
var isPromise = function isPromise(x) {
  return x instanceof Promise;
};
var reduceObj = function reduceObj(fn, initial, obj) {
  return Object.keys(obj).reduce(function (acc, key) {
    return fn(acc, obj[key], key);
  }, initial);
};
var mapObj = function mapObj(fn, obj) {
  return reduceObj(function (obj, value, key) {
    return Object.assign({}, obj, _defineProperty({}, key, fn(value, key)));
  }, {}, obj);
};

/*
given a stylesheet (an object containing modifiers -> styles), we want to return all of the
styles that match the current set of modifiers
(
  {
    default: {
      root: {
        color: 'black',
        backgroundColor: 'white',
      },
    },
    foo: {
      root: {
        color: 'blue',
      },
    },
  },
  [
    {
      foo: true,
    },
  ],
) => [
  {
    root: {
      color: 'black',
      backgroundColor: 'white',
    },
  },
  {
    root: {
      color: 'blue',
    },
  },
]
*/
// eslint-disable-next-line max-len

var appendStylesForKey = function appendStylesForKey(stylesheet, props) {
  return function (acc, value, key) {
    if (value && stylesheet[key]) {
      return acc.concat(stylesheet[key]);
    }

    return acc;
  };
}; // eslint-disable-next-line max-len


var appendStylesForProps = function appendStylesForProps(stylesheet) {
  return function (acc, props) {
    return reduceObj(appendStylesForKey(stylesheet), acc, props);
  };
};

var handleAsyncModifier = function handleAsyncModifier(modifier) {
  if (typeof modifier !== 'function') {
    return modifier;
  }

  var styles = modifier();

  if (!isPromise(styles)) {
    return styles;
  } // @ts-ignore


  var p = styles.then(function (styles) {
    return styles.default || styles;
  });
  return p;
};

var getStyles = function getStyles(stylesheet, modifiers) {
  var initial = [stylesheet.default || {}];
  var styles = modifiers.reduce(appendStylesForProps(stylesheet), initial).map(handleAsyncModifier);
  return styles;
};

var autoPrefix = function autoPrefix(namepsacedStyles) {
  return mapObj(function (styles) {
    var initial = {};
    return reduceObj(function (acc, _value, _key) {
      // opt out of grid prefixing, causes too many IE11 issues
      // modern browsers support the standard syntax
      if (_key === 'display' && _value === 'grid') {
        return Object.assign({}, acc, _defineProperty({}, _key, _value));
      }

      var key = supportedProperty(_key);
      var value = supportedValue(key, _value);
      return Object.assign({}, acc, _defineProperty({}, key, value));
    }, initial, styles);
  }, namepsacedStyles);
};

var isAsyncStyle = isPromise;
/*
simply just reduces an array of styles by merging them together
([
  {
    root: {
      color: 'black',
    },
  },
  {
    root: {
      color: 'blue',
    },
  },
]) => {
  root: {
    color: 'blue',
  },
}
*/

var reduceStyles = function reduceStyles(modifiers) {
  var reduced = modifiers.reduce(mergeDeep, {});
  return autoPrefix(reduced);
}; // calls reduceStyles but only for styles that aren't promises


var reduceSyncStyles = function reduceSyncStyles(styles) {
  var syncStyles = styles.filter(function (styles) {
    return !isAsyncStyle(styles);
  });
  return reduceStyles(syncStyles);
}; // checks if any styles are promises

var hasAsyncStyles = function hasAsyncStyles(styles) {
  return styles.some(isAsyncStyle);
};

/*
takes the modifiers parameter and normalizes it
if omitted, it defaults to an empty array
if an object is supplied, it is wrapped in an array
for each modifier, the key/value are combined an added as a new key
({
  thing: true,
  foo: 'bah',
}) => [
  {
    thing: true,
    foo: 'bah',
    'thing.true': true,
    'foo.bah': true,
  },
]
*/

var normalizeModifiers = function normalizeModifiers(_modifiers) {
  var modifiers = [].concat(_modifiers || []);
  return modifiers.map(function (props) {
    var initial = {};
    return reduceObj(function (acc, value, key) {
      var _Object$assign2;

      if (!isPrimitive(value)) {
        return Object.assign({}, acc, _defineProperty({}, key, value));
      }

      return Object.assign({}, acc, (_Object$assign2 = {}, _defineProperty(_Object$assign2, key, value), _defineProperty(_Object$assign2, "".concat(key, "-").concat(value), true), _Object$assign2));
    }, initial, props);
  });
};

/*
this function just flattens and returns all of the modifier values
we then use this as the dep array when calling our react hooks for caching
this means that if you're using any of your props in your styles, you should also pass them
in as modifiers
([ { foo: 'bah' }, { age: 45 }]) => [ 'bah', 45 ]
*/

var getModifierValues = function getModifierValues(modifiers) {
  return modifiers.reduce(function (acc, modifiers) {
    return reduceObj(function (acc, value) {
      return acc.concat(value);
    }, acc, modifiers);
  }, []);
};

var useStyles = function useStyles(stylesheet, _modifiers) {
  var modifiers = normalizeModifiers(_modifiers);
  var deps = getModifierValues(modifiers); // get all applicable styles, we use the given modifiers to determine when to re-calculate this
  // this can contain both sync and async styles

  var allStyles = useMemo(function () {
    return getStyles(stylesheet, modifiers);
  }, deps); // calculate the initial styles to use, we should only ever do this once

  var initialStyles = useMemo(function () {
    return reduceSyncStyles(allStyles);
  }, []);

  var _useState = useState(initialStyles),
      _useState2 = _slicedToArray(_useState, 2),
      styles = _useState2[0],
      setStyles = _useState2[1]; // whenever allStyles is recalculated we want to update the state
  // and also process any aysnc styles


  useEffect(function () {
    var mounted = true;
    setStyles(reduceSyncStyles(allStyles));

    if (hasAsyncStyles(allStyles)) {
      // resolve all async styles and then update the state with the resolved value
      Promise.all(allStyles).then(function (x) {
        if (mounted) {
          var result = reduceStyles(x);
          setStyles(result);
        }
      });
    } // if the promise resolves after the component has unmounted
    // we don't want to trigger the setState action


    return function () {
      mounted = false;
    };
  }, [allStyles]);
  return {
    styles: styles
  };
};

var removeInteraction = omitProps(['active', 'hover', 'focus', 'hocus']);

export { removeInteraction, useStyles, withInteraction as withHover, withInteraction };
